<template>
    <v-container class="pa-12 fill-height align-start" fluid>
        <v-card outlined width="100%">
            <div class="d-flex align-center justify-space-between pa-4">
                <span class="title">Sessions</span>
                <v-btn small depressed color="primary" @click="showFilter = !showFilter">Toggle filter</v-btn>
            </div>
            <v-card flat color="grey lighten-4" v-if="showFilter">
                <v-row class="px-4" align="center">
                    <v-col cols="12" md="6" lg="3" xl="2">
                        <v-menu
                            ref="fromDatemenu"
                            v-model="fromDatemenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    readonly
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    v-model="filterObject.fromDate"
                                    label="From"
                                    prepend-inner-icon="mdi-calendar"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker v-model="filterObject.fromDate" color="primary" @input="fromDatemenu = false" no-title scrollable></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="6" lg="3" xl="2">
                        <v-menu
                            ref="toDatemenu"
                            v-model="toDatemenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    readonly
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    v-model="filterObject.toDate"
                                    label="To"
                                    prepend-inner-icon="mdi-calendar"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker v-model="filterObject.toDate" color="primary" @input="toDatemenu = false" no-title scrollable></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="6" lg="3" xl="3">
                        <v-select 
                            dense
                            outlined
                            hide-details
                            clearable
                            label="Filter by project"
                            :items="projects"
                            item-text="title"
                            item-value="id"
                            v-model="filterObject.projectId"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6" lg="3" xl="3">
                        <v-select 
                            dense
                            outlined
                            hide-details
                            clearable
                            label="Filter by status"
                            :items="filterStatusOptions"
                            @change="statusFilterEventHandler"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6" lg="12" xl="2" class="d-flex flex-shrink-0 flex-grow-0">
                        <v-btn 
                            depressed 
                            small 
                            height="38px" 
                            color="primary"
                            class="mr-2"
                            @click="filterBids"
                        >Filter</v-btn>
                        <v-btn 
                            depressed 
                            small 
                            height="38px" 
                            color="secondary"
                            @click="resetFilter"
                        >Reset</v-btn>
                    </v-col>
                </v-row>
            </v-card>
            <v-data-table 
                :loading="loadingBids"
                loading-text="Loading... Please wait"
                :headers="headers"
                :items="bids"
            >
                <template v-slot:item.name="{ item }">
                    {{ item.user.firstName }} {{ item.user.lastName }}
                </template>
                <template v-slot:item.title="{ item }">
                    {{ item.bid.title }}
                </template>
                <template v-slot:item.date="{ item }">
                    {{ dateFormat(item.bid.date.localTime) }}
                </template>
                <template v-slot:item.project="{ item }">
                    {{ item.project.title }}
                </template>
                <template v-slot:item.status="{ item }">
                    <template v-if="item.bid.isPending">
                        <v-chip small outlined>Pending</v-chip>
                    </template>
                    <template v-else>
                        <v-chip small outlined color="primary" v-if="item.bid.isApproved">Approved</v-chip>
                        <v-chip small outlined color="secondary" v-else>Rejected</v-chip>
                    </template>
                </template>
                <template v-slot:item.actions="{ item }">
                    <v-btn
                        depressed
                        small
                        color="primary"
                        :to="`/${$i18n.locale}/administrator/bid/review/${item.bid.id}`"
                    >View details</v-btn>
                </template>
                <template v-slot:no-data>
                    <div class="body-2">No data available</div>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import { mapGetters } from 'vuex'

    export default {
        name: 'creativeBids',
        data: () => ({
            bids: [],
            projects: [],
            headers: [
                { text: 'Name', value: 'name' },
                { text: 'Title', value: 'title' },
                { text: 'Date', value: 'date' },
                { text: 'Project', value: 'project' },
                { text: 'Date', value: 'date', align: 'center' },
                { 
                    text: 'Status', 
                    value: 'status', 
                    sortable: false,
                    width: '100',
                    align: 'center'
                },
                { 
                    text: 'Actions', 
                    value: 'actions', 
                    sortable: false,
                    width: '200',
                    align: 'center'
                }
            ],
            showFilter: false,
            fromDatemenu: false,
            toDatemenu: false,
            filterStatusOptions: [
                {
                    text: 'All',
                    value: 0,
                },
                {
                    text: 'Is approved',
                    value: 1,
                },
                {
                    text: 'Is pending',
                    value: 2,
                }
            ],
            filterObject: {
                projectId: null,
                isApproved: null,
                isPending: null,
                fromDate: null,
                toDate: null
            },
            snackbar: {
                show: true,
                message: ''
            },
            loadingBids: false,
            loadingProjects: true
        }),
        computed: {
            ...mapGetters(['user', 'languages', 'defaultLanguage'])
        },
        mounted() {
            this.fetchBids()
            this.fetchProjects()
        },
        methods: {
            async fetchBids() {
                this.loadingBids = true
                await this.axios.get('/ProjectBid/GetAll', {
                    params: {
                        languageId: 1,
                        projectId: this.filterByProjectId
                    }
                })
                .then(response => {
                    this.bids = response.data
                    this.loadingBids = false
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loadingBids = false
                })
            },
            async fetchProjects() {
                await this.axios.get('/Project/GetProjectList', {
                    params: {
                        languageId: this.defaultLanguage.id
                    }
                })
                .then(response => {
                    this.projects = response.data
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
                this.loadingProjects = false
            },
            async filterBids() {
                this.loadingBids = true
                await this.axios.get('/Projectbid/GetAllByFilters', {
                    params: {
                        languageId: this.user.preferedLanguage,
                        projectId: this.filterObject.projectId,
                        isApproved: this.filterObject.isApproved,
                        isPending: this.filterObject.isPending,
                        fromDate: this.filterObject.fromDate ? new Date(this.filterObject.fromDate).toISOString() : null,
                        toDate: this.filterObject.toDate ? new Date(this.filterObject.toDate).toISOString() : null
                    }
                })
                .then(response => {
                    this.bids = response.data
                    this.loadingBids = false
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loadingBids = false
                })
            },
            resetFilter() {
                this.filterObject.projectId = null
                this.filterObject.isApproved = null
                this.filterObject.isPending = null
                this.filterObject.fromDate = null
                this.filterObject.toDate = null
                this.fetchBids()
            },
            dateFormat(date) {
                return moment(date).format('MM/DD/YYYY')
            },
            statusFilterEventHandler(value) {
                switch(value) {
                    case 0:
                        this.filterObject.isApproved = null 
                        this.filterObject.isPending = null 
                        break
                    case 1:
                        this.filterObject.isApproved = true 
                        this.filterObject.isPending = null 
                        break
                    case 2:
                        this.filterObject.isApproved = null
                        this.filterObject.isPending = true 
                        break
                    default:
                        this.filterObject.isApproved = null 
                        this.filterObject.isPending = null 
                }
            }
        }
    }
</script>

<style lang="scss" scoped>

</style>